import type { AccountInfo } from "@azure/msal-browser";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import * as Sentry from "@sentry/react";

import {
  login,
  setAzureToken,
  setToken
} from "@/redux/authorization/authorization.slice";
import { resetState } from "@/utils/redux";
import { userApi } from "@/redux/user/user.api";
import { store } from "@/redux";
import { checkEntraAuthGate } from "@/utils/statsig";
import { debug } from "@/utils/debug";
import { msalInstance, persistActiveAccountId } from "./msal-instance";
import { msalConfig, scopes } from "./authConfig";
import {
  clearEntraLoginHint,
  getEntraLoginHint,
  setEntraLoginHint
} from "./login-hint";

interface BootstrapOptions {
  isSuperseded: () => boolean;
}

const applyRecoverySuccess = (
  result: {
    accessToken: string;
    expiresOn?: Date | null;
    account?: { username: string; homeAccountId: string } | null;
  },
  loginHint: string
) => {
  const recoveredAccount = result.account;
  if (recoveredAccount) {
    msalInstance.setActiveAccount(recoveredAccount as AccountInfo);
    persistActiveAccountId(recoveredAccount.homeAccountId);
  }

  store.dispatch(resetState());
  store.dispatch(userApi.util.resetApiState());
  store.dispatch(setToken(result.accessToken));
  store.dispatch(setAzureToken(result.accessToken));
  store.dispatch(login());
  setEntraLoginHint(recoveredAccount?.username ?? loginHint);

  debug.log("[bootstrap] Session recovered successfully", {
    username: recoveredAccount?.username ?? loginHint,
    expiresOn: result.expiresOn?.toISOString() ?? "unknown"
  });
};

/**
 * Attempts to recover a session when MSAL has evicted all accounts (e.g. after
 * prolonged inactivity) but we still have a loginHint from a previous login.
 *
 * 1. Try `ssoSilent` (hidden iframe, no UI) — works when the Entra SSO session
 *    cookie is still valid and 3rd-party cookies are allowed.
 * 2. Fall back to `loginRedirect` with `loginHint` — Entra will auto-sign-in
 *    if the SSO session is valid, otherwise prompt for credentials.
 */
const recoverSessionFromLoginHint = async (
  loginHint: string,
  options: BootstrapOptions
): Promise<void> => {
  try {
    debug.log("[bootstrap] Trying ssoSilent with loginHint");
    const ssoResult = await msalInstance.ssoSilent({
      scopes,
      loginHint
    });

    if (options.isSuperseded()) return;

    if (ssoResult.accessToken) {
      applyRecoverySuccess(ssoResult, loginHint);
      return;
    }
  } catch (ssoError) {
    debug.log("[bootstrap] ssoSilent failed — falling back to loginRedirect", {
      errorCode: (ssoError as { errorCode?: string })?.errorCode
    });
  }

  if (options.isSuperseded()) return;

  debug.log("[bootstrap] Redirecting to Entra with loginHint for recovery");
  await msalInstance.loginRedirect({
    scopes,
    loginHint
  });
};

/**
 * Seeds the Redux auth state from cached MSAL account before the router mounts.
 * Runs only when Entra gate is on and an active MSAL account exists.
 * When all MSAL accounts have been evicted but a loginHint is persisted,
 * attempts recovery via ssoSilent or loginRedirect.
 */
export const bootstrapEntraSession = async (
  options: BootstrapOptions
): Promise<void> => {
  const isEntra = checkEntraAuthGate();
  if (!isEntra) {
    debug.log("[bootstrap] Entra gate OFF — skipping bootstrap");
    return;
  }

  const account = msalInstance.getActiveAccount();
  const persistedAuth = store.getState().auth;
  if (!account) {
    const loginHint = getEntraLoginHint();
    const hasStalePortalAuth =
      persistedAuth.isAuthorized ||
      Boolean(persistedAuth.token) ||
      Boolean(persistedAuth.azureToken);
    debug.log("[bootstrap] No cached MSAL account", {
      cachedAccountCount: msalInstance.getAllAccounts().length,
      hasRecoveryHint: Boolean(loginHint),
      hasStalePortalAuth
    });
    if (!loginHint) {
      if (hasStalePortalAuth) {
        store.dispatch(resetState());
        store.dispatch(userApi.util.resetApiState());
        debug.log("[bootstrap] Stale Portal auth — redirecting to Entra", {
          recoveryAction: "loginRedirect"
        });
        await msalInstance.loginRedirect({ scopes });
        return;
      }
      debug.log(
        "[bootstrap] No active MSAL account and no loginHint — skipping bootstrap"
      );
      return;
    }

    debug.log(
      "[bootstrap] No MSAL account but loginHint exists — attempting recovery",
      { loginHint }
    );

    await recoverSessionFromLoginHint(loginHint, options);
    return;
  }

  debug.log("[bootstrap] Bootstrapping session from MSAL cache", {
    username: account.username
  });

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes, account });

    if (options.isSuperseded()) {
      debug.log("[bootstrap] Superseded after acquireTokenSilent — aborting");
      return;
    }

    const accessToken = result.accessToken;
    if (!accessToken) {
      debug.warn("[bootstrap] acquireTokenSilent returned empty accessToken");
      return;
    }

    const previousHint = getEntraLoginHint();
    const currentUsername = account.username;

    if (!previousHint || previousHint !== currentUsername) {
      debug.warn("[bootstrap] Identity changed — clearing stale state", {
        previous: previousHint,
        current: currentUsername
      });
      store.dispatch(resetState());
      store.dispatch(userApi.util.resetApiState());
    }

    store.dispatch(setToken(accessToken));
    store.dispatch(setAzureToken(accessToken));
    store.dispatch(login());
    setEntraLoginHint(currentUsername);
    persistActiveAccountId(account.homeAccountId);

    debug.log("[bootstrap] Session bootstrapped successfully", {
      username: currentUsername,
      expiresOn: result.expiresOn?.toISOString() ?? "unknown"
    });
  } catch (error) {
    if (options.isSuperseded()) {
      debug.log("[bootstrap] Superseded during error handling — aborting");
      return;
    }

    const errorCode = (error as { errorCode?: string })?.errorCode;
    const isRecoverable =
      error instanceof InteractionRequiredAuthError ||
      errorCode === "timed_out" ||
      errorCode === "monitor_window_timeout";

    if (isRecoverable) {
      debug.log(
        "[bootstrap] Recoverable error — redirecting to acquire token",
        { errorCode }
      );
      await msalInstance.acquireTokenRedirect({
        scopes,
        account,
        loginHint: account.username
      });
      return;
    }

    debug.error("[bootstrap] Unrecoverable error during bootstrap:", error);
    if (!import.meta.env.DEV) {
      Sentry.captureException(error, {
        tags: {
          authPhase: "bootstrapEntraSession",
          msalErrorCode: errorCode ?? "unknown"
        }
      });
    }

    store.dispatch(resetState());
    store.dispatch(userApi.util.resetApiState());
    clearEntraLoginHint();
    msalInstance.setActiveAccount(null);
    try {
      await msalInstance.clearCache({ account });
    } catch (clearError) {
      debug.error("[bootstrap] clearCache failed:", clearError);
      void msalInstance.logoutRedirect({
        account,
        postLogoutRedirectUri:
          msalConfig.auth.postLogoutRedirectUri ?? msalConfig.auth.redirectUri
      });
    }
  }
};
